import { useState } from "react";
import Container from "../../../common/Container";
import BuildingBlocksRouter from "./BuildingBlocksRouter";
import notificationService from "../../../../services/NotificationService";
import BlueScrollBackToTopButton from "../../../common/BlueScrollBackToTopButton";
import { useTranslation } from "react-i18next";
const UploadDisplay = ({
    uploadedTemplates,
    userSettings,
    selectedTemplates,
    deleteSelectedTemplate,
    deleteUploadedTemplate,
    addFirstUploadedTemplate,
    replaceUploadedTemplate,
    addFirstSelectedTemplate,
    replaceSelectedTemplate,
    updateSelectedTemplate,
    priceOptions,
}) => {
    const { t } = useTranslation("templatesPage");

    const [isButtonUploadGray, setIsButtonUploadGray] = useState(true);
    const [uploadFileData, setUploadFileData] = useState(null);
    const [nameInput, setNameInput] = useState("");
    const [isUpdatingImage, setIsUpdatingImage] = useState(false);
    const [isPublicUpload, setIsPublicUpload] = useState(true);
    const [uploadMessage, setUploadMessage] = useState("");

    const removeUploadedTemplateQuestion = (item) => {
        notificationService.setMessage(
            t("uploadPageDeleteQuestion", { title: item.title }),
        );
        notificationService.setTitle(t("deleteTitle"));
        notificationService.onClick(() => {
            //reset the window
            notificationService.clearAll();
            removeUploadedTemplate(item);
        });
    };

    const getSelectedTemplateItem = (item) => {
        return selectedTemplates.find(
            (selected) => item.thumbnailUrl === selected.thumbnailUrl,
            // Because the id's differ we look the selectedTemplate up
        );
    };

    const removeUploadedTemplate = async (item) => {
        if (!item || !item.id) {
            notificationService.setMessage(t("uploadPageRemovingFailed"));
            return; // Early return if item ID is missing
        }

        // Step 1: set state to loading
        replaceUploadedTemplate({ ...item, isLoading: true });

        // Step 2: get selected template item
        const selectedItem = getSelectedTemplateItem(item);

        // Step 3: ensure the selected item and uploaded item are deleted
        const selectedDeleted = selectedItem
            ? await deleteSelectedTemplate(selectedItem)
            : true;
        if (selectedDeleted) await deleteUploadedTemplate(item);

        // Stel 4: set the counter for display bar
        replaceUploadedTemplate({ ...item, isLoading: false });
    };

    const selectUploadedTemplate = async (item) => {
        if (!item || !item.id) {
            notificationService.setMessage(t("uploadPageSelectionFailed"));
            return; // Early return if item ID is missing
        }

        // Step 1: set state to loading
        replaceUploadedTemplate({ ...item, isLoading: true });

        // Step 2: Call the service to add the selected template
        const addedTemplate = await addFirstSelectedTemplate(item);

        // Step 3: add thumbnailUrl and update the item
        replaceSelectedTemplate({
            ...addedTemplate,
            thumbnailUrl: item.thumbnailUrl,
        });

        // Step 4: set state of loading
        replaceUploadedTemplate({
            ...item,
            isLoading: false,
            price: addedTemplate.price,
        });
    };

    const removeSelectedUploadedTemplateFromSelection = async (item) => {
        if (!item || !item.id) {
            notificationService.setMessage(t("uploadPageUnselectingFailed"));
            return; // Early return if item ID is missing
        }

        // Step 1: set state to loading
        replaceUploadedTemplate({ ...item, isLoading: true });

        // Step 2: get selected template item
        const selectedItem = getSelectedTemplateItem(item);

        // Step 3: Delete selected template
        await deleteSelectedTemplate(selectedItem);

        // Step 4: Update uploaded template and set loading state
        item.type = "UPLOADED";
        item.isLoading = false;

        replaceUploadedTemplate(item);
    };

    const resetDisplayUploadImage = () => {
        setNameInput("");
        setIsUpdatingImage(false);
        setUploadMessage(t("uploadPageFinishedUploading"));
        setUploadFileData(null);
        setTimeout(() => setUploadMessage(""), 500);
    };

    const uploadTemplate = async () => {
        // Step 1: set state to loading
        setIsUpdatingImage(true);

        // Step 2: validate input
        if (uploadFileData && nameInput) {
            // Step 2 -> 1: display message
            setUploadMessage(t("uploadPageUploading"));

            // Step 2 -> 2: Add image
            addFirstUploadedTemplate(nameInput, isPublicUpload, uploadFileData);

            // Step 2 -> 3: Reset display
            resetDisplayUploadImage();
        } else setIsUpdatingImage(false);
    };

    const setImageAsBinaryString = (readerEvent) => {
        let binaryString = readerEvent.target.result;
        setUploadFileData(btoa(binaryString));
    };

    const convertFile = (e, dragEvent) => {
        const mb = 3;
        // This is math I found on stack overflow to convert 3 mb into bits
        // When i first implemented it I also posted the source and how it works
        // We only need to change the first value which is 3 if we want a different mb size
        const maxAllowedSize = mb * 1024 * 1024;
        let file = dragEvent ? e.files[0] : e.target.files[0];

        if (file && file.size >= maxAllowedSize) {
            notificationService.setMessage(t("uploadPageFileTooLarge"));
            return;
        }

        if (!file.type.match(/image.*/)) {
            notificationService.setMessage(t("uploadPageFileIsNotAnimage"));
            return;
        }

        if (file && file.size <= maxAllowedSize) {
            const reader = new FileReader();
            reader.onload = setImageAsBinaryString;
            reader.readAsBinaryString(file);
        }
    };

    // remove selected image from upload preview
    const removeSetImage = () => setUploadFileData(false);

    //dynamic upload button
    if (
        uploadFileData &&
        nameInput.length > 0 &&
        isButtonUploadGray &&
        !isUpdatingImage
    ) {
        setIsButtonUploadGray(false);
    }

    if (isUpdatingImage && !isButtonUploadGray) {
        setIsButtonUploadGray(true);
    }

    //main content display
    let content = (
        <BuildingBlocksRouter
            uploadedTemplates={uploadedTemplates}
            userSettings={userSettings}
            setUploadFileData={setUploadFileData}
            setNameInput={setNameInput}
            nameInput={nameInput}
            isButtonUploadGray={isButtonUploadGray}
            uploadTemplate={uploadTemplate}
            removeUploadedTemplateQuestion={removeUploadedTemplateQuestion}
            selectUploadedTemplate={selectUploadedTemplate}
            removeSelectedUploadedTemplateFromSelection={
                removeSelectedUploadedTemplateFromSelection
            }
            convertFile={convertFile}
            uploadMessage={uploadMessage}
            uploadFileData={uploadFileData}
            selectedTemplates={selectedTemplates}
            removeSetImage={removeSetImage}
            isPublicUpload={isPublicUpload}
            setIsPublicUpload={setIsPublicUpload}
            priceOptions={priceOptions}
            updateSelectedTemplate={updateSelectedTemplate}
        />
    );

    return (
        <div className="scrollableContainer">
            <BlueScrollBackToTopButton buttonShowThreshold={1000} left={true} />
            <Container
                content={content}
                isNoBackground={true}
                animationLeft={true}
            />
        </div>
    );
};

export default UploadDisplay;
